import React from 'react';
import { clsx } from 'clsx';
import { ReasonBadge } from '@/components/ui/ReasonBadge';
import { StatusPill } from '@/components/ui/StatusPill';
import { signedToneClass, signedNumber, CHART_RANGES, type ChartRange } from './history-utils';

// LR-498: the right-pane summary header (zone ①), tuned per counterparty type. Purely
// presentational — the HistoryShell resolves every value (hero, chips, chart) per
// tab × view and passes them in. The hero is a custom mono figure (28/700) to match the
// design exactly; the lr figure tiers are a fixed 34/21/22/14 scale that doesn't include it.

export interface DetailChip {
  label: string;
  value: React.ReactNode;
  tone?: 'pos' | 'neg' | 'neutral';
}

export interface DetailChartSection {
  title: string;
  subtitle?: string;
  node: React.ReactNode;
  /** The active range window; the selector is functional when onRangeChange is provided. */
  range?: ChartRange;
  onRangeChange?: (range: ChartRange) => void;
}

export interface DetailHeaderProps {
  title: string;
  pill: { tone: 'accent' | 'warn'; label: string };
  heroLabel: string;
  /** Resolved figure; null → "—". */
  heroValue: number | null;
  heroMeta?: React.ReactNode;
  /** Sign-colour the hero (default true). */
  heroColored?: boolean;
  /** LR-498 v2 (T1-4): preformatted hero text — overrides the numeric heroValue render (e.g. the
   *  full-precision `moneyFull(net) + " " + currency`). Wins even when heroValue is null. */
  heroValueText?: string;
  /** LR-498 v2 (T1-3): explicit hero tone, highest priority (overrides heroColored/signedToneClass).
   *  'muted' → faint (a below-MTA run that moves no cash). */
  heroTone?: 'pos' | 'neg' | 'neutral' | 'muted';
  /** LR-498 v2 (T1-3): inline amber pill after the hero value, e.g. "below MTA · no transfer". */
  heroBadge?: { label: string; tone: 'warn'; reason?: string };
  chips?: DetailChip[];
  /** A row of its own under the hero that wraps — for a list of unbounded length, which the
   *  right-hand chips row (no wrap) would push past the header. */
  breakdown?: { title: string; chips: DetailChip[] };
  chart?: DetailChartSection;
}

const CHIP_TONE: Record<NonNullable<DetailChip['tone']>, string> = {
  pos: 'text-lr-pos',
  neg: 'text-lr-neg',
  neutral: 'text-lr-fg-2',
};

const HERO_TONE: Record<NonNullable<DetailHeaderProps['heroTone']>, string> = {
  pos: 'text-lr-pos',
  neg: 'text-lr-neg',
  neutral: 'text-lr-fg',
  muted: 'text-lr-faint',
};

const Chip: React.FC<DetailChip> = ({ label, value, tone = 'neutral' }) => (
  <div className="rounded-[7px] border border-lr-border bg-lr-surface-2 px-[13px] py-[9px]">
    <div className="text-[10px] uppercase tracking-[.04em] text-lr-faint">{label}</div>
    <div className={clsx('mt-[3px] font-mono text-sm font-semibold', CHIP_TONE[tone])}>{value}</div>
  </div>
);

export const DetailHeader: React.FC<DetailHeaderProps> = ({
  title,
  pill,
  heroLabel,
  heroValue,
  heroMeta,
  heroColored = true,
  heroValueText,
  heroTone,
  heroBadge,
  chips,
  breakdown,
  chart,
}) => (
  <div className="border-b border-lr-border px-6 pb-4 pt-[18px]">
    <div className="flex items-center gap-2.5">
      <span className="text-[19px] font-bold text-lr-fg">{title}</span>
      <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
    </div>

    <div className="mt-3 flex items-end justify-between gap-5">
      <div className="min-w-0">
        <div className="text-[10.5px] font-medium uppercase tracking-[.05em] text-lr-faint">
          {heroLabel}
        </div>
        <div className="mt-[3px] flex items-baseline gap-2.5">
          <span
            className={clsx(
              'font-mono text-[28px] font-bold leading-none',
              heroTone
                ? HERO_TONE[heroTone]
                : heroValue == null
                  ? 'text-lr-faint'
                  : heroColored
                    ? signedToneClass(heroValue)
                    : 'text-lr-fg'
            )}
          >
            {heroValueText ?? (heroValue == null ? '—' : signedNumber(heroValue))}
          </span>
          {heroBadge && (
            <ReasonBadge
              label={heroBadge.label}
              reason={heroBadge.reason}
              className="whitespace-nowrap rounded-md bg-lr-warn-weak px-[9px] py-[3px] text-[10.5px] text-lr-warn"
            />
          )}
        </div>
        {heroMeta != null && heroMeta !== '' && (
          <div className="mt-[2px] text-xs text-lr-faint">{heroMeta}</div>
        )}
      </div>
      {chips && chips.length > 0 && (
        <div className="flex shrink-0 gap-2.5">
          {chips.map((chip, i) => (
            <Chip key={`${chip.label}-${i}`} {...chip} />
          ))}
        </div>
      )}
    </div>

    {breakdown && breakdown.chips.length > 0 && (
      <div className="mt-3">
        <div className="text-[10.5px] font-semibold uppercase tracking-[.05em] text-lr-faint">
          {breakdown.title}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-2.5">
          {breakdown.chips.map((chip, i) => (
            <Chip key={`${chip.label}-${i}`} {...chip} />
          ))}
        </div>
      </div>
    )}

    {chart && (
      <>
        <div className="mb-0.5 mt-3.5 flex items-center gap-2.5">
          <span className="text-[10.5px] font-semibold uppercase tracking-[.05em] text-lr-faint">
            {chart.title}
          </span>
          {chart.subtitle && <span className="text-[11px] text-lr-faint">{chart.subtitle}</span>}
          <span className="h-px flex-1 bg-lr-border" />
          {CHART_RANGES.map((r) => {
            const active = r === chart.range;
            return (
              <button
                key={r}
                type="button"
                aria-pressed={active}
                onClick={() => chart.onRangeChange?.(r)}
                className={clsx(
                  'rounded-[5px] px-2 py-0.5 text-[11px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent',
                  active
                    ? 'bg-lr-bg font-semibold text-lr-fg'
                    : 'font-medium text-lr-muted hover:text-lr-fg-2'
                )}
              >
                {r}
              </button>
            );
          })}
        </div>
        {chart.node}
      </>
    )}
  </div>
);
